import { ChangeDetectionStrategy, Component, Inject, OnInit, inject, DestroyRef } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { CoreService } from '../../services/core.service';
import { take } from 'rxjs/operators';
import { Router, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FacebookPostComponent } from '../../components/templates/facebook-post/facebook-post.component';
import { FacebookStoryComponent } from '../../components/templates/facebook-story/facebook-story.component';
import { InstagramPostComponent } from '../../components/templates/instagram-post/instagram-post.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectorRef } from '@angular/core';

import {
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';

import {
  LatestPost,
  LatestPostsComponent,
} from '../../components/sections/latest-posts/latest-posts.component';
import {
  LatestAlert,
  LatestAlertsComponent,
} from '../../components/sections/latest-alerts/latest-alerts.component';

@Component({
  selector: 'app-delete-confirm-dialog',
  standalone: true,
  template: `
    <h2 mat-dialog-title class="text-red-500">Confirm Delete</h2>

    <mat-dialog-content>
      Are you sure you want to delete this post?
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Cancel</button>
      <button mat-button color="warn" [mat-dialog-close]="'confirm'">Delete</button>
    </mat-dialog-actions>
  `,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButtonModule,
    MatTooltipModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeleteConfirmDialogComponent {}

@Component({
  selector: 'app-social-ai-preview',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    FacebookPostComponent,
    InstagramPostComponent,
    FacebookStoryComponent,
    LatestPostsComponent,
    LatestAlertsComponent,
  ],
  templateUrl: './social-ai-preview.component.html',
  styleUrls: ['./social-ai-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class SocialAiPreviewComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);

  renderPreview = true;

  id: string | null = null;
  nextId: string | null = null;
  prevId: string | null = null;
  tag: string | null = '';
  nextTag: string | null = '';
  prevTag: string | null = '';

  companyID: number = 0;
  postStatus: string = '';
  enableRepeat: number | null = null;
  enableEdit: number = 1;
  enableReuse: number = 1;
  enableDelete: number = 1;
  returnPage: number | null = null;

  maxSidebarCards = 3;
  posts: LatestPost[] = [];
  alerts: LatestAlert[] = [];
  naPlaceholder = './assets/images/na.png';

  private readonly LATEST_POSTS_KEY = 'latestPosts';
  private readonly LATEST_ALERTS_KEY = 'latestAlerts';

  constructor(
    private router: Router,
    private location: Location,
    private route: ActivatedRoute,
    private readonly coreService: CoreService,
  ) {}

  ngOnInit() {
    this.companyID = this.coreService.user?.companyID || 0;

    this.loadSidebarPosts();
    this.loadAlertsFromStorage();

    this.coreService.latestPosts$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(posts => {
        if (posts && posts.length > 0) {
          this.posts = posts.slice(0, this.maxSidebarCards);
        }
      });

    this.coreService.latestAlerts$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(alerts => {
        if (alerts && alerts.length > 0) {
          this.alerts = alerts.slice(0, this.maxSidebarCards);
        }
      });

    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        this.returnPage = params['returnPage'] ? Number(params['returnPage']) : null;
      });

    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(pm => {
        this.tag = pm.get('tag');
        this.id = pm.get('id');

        if (this.id) {
          this.loadActions(this.id);
        }

        this.renderPreview = false;
        this.cdr.detectChanges();
        this.renderPreview = true;
      });
  }

  loadActions(id: string): void {
    this.coreService
      .getData(`/post/actions/${this.companyID}/${id}`)
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          if (!res) {
            this.coreService.showSnackbar('Post not found.');
            return;
          }

          this.postStatus = res.post_status ?? '';
          this.enableRepeat = typeof res.enable_repeat === 'number'
            ? res.enable_repeat
            : null;

          this.enableEdit = 1;
          this.enableReuse = 1;
          this.enableDelete = 1;

          this.nextId = res.next_id ?? null;
          this.nextTag = res.next_tag ?? null;

          this.prevId = res.prev_id ?? null;
          this.prevTag = res.prev_tag ?? null;
        },
        error: (err: any) => {
          console.error('Failed to load post actions:', err);
          this.coreService.showSnackbar('Could not load post actions.');
        },
      });
  }

  get isEditDisabled(): boolean {
    const status = (this.postStatus || '').toLowerCase();
    const repeat = this.enableRepeat ?? 0;

    return (
      status === 'postedoninstagram' ||
      status === 'postedonfacebook' ||
      repeat === -1
    );
  }

  get isReuseDisabled(): boolean {
    const status = (this.postStatus || '').toLowerCase();

    return (
      status === 'draft' ||
      status === 'fail' ||
      status === 'permanently failed'
    );
  }

  get isDeleteDisabled(): boolean {
    const status = (this.postStatus || '').toLowerCase();

    return status !== 'draft';
  }

  refreshPage() {
    window.location.reload();
  }

  editPost(id: any) {
    this.router.navigate(['/edit-post', id], {
      queryParams: { returnPage: this.returnPage },
    });
  }

  reusePost() {
    this.router.navigate(['/reuse-post', this.id], {
      queryParams: { returnPage: this.returnPage },
    });
  }

  nextPost(): void {
    if (!this.nextId || !this.nextTag) return;

    this.router.navigate(['/preview-post', this.nextTag, this.nextId], {
      queryParams: { returnPage: this.returnPage },
    });
  }

  prevPost(): void {
    if (!this.prevId || !this.prevTag) return;

    this.router.navigate(['/preview-post', this.prevTag, this.prevId], {
      queryParams: { returnPage: this.returnPage },
    });
  }

  viewList() {
    this.router.navigate(['/posts'], {
      queryParams: { page: this.returnPage ?? 1 },
    });
  }

  private loadSidebarPosts(): void {
    if (typeof window === 'undefined') {
      this.posts = [];
      return;
    }

    const raw = localStorage.getItem(this.LATEST_POSTS_KEY);

    if (!raw) {
      this.posts = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw) as LatestPost[] | null;

      this.posts = Array.isArray(parsed)
        ? parsed.slice(0, this.maxSidebarCards)
        : [];
    } catch (e) {
      console.error('Failed to parse latest posts from localStorage', e);
      this.posts = [];
    }
  }

  private loadAlertsFromStorage(): void {
    if (typeof window === 'undefined') {
      this.alerts = [];
      return;
    }

    const raw = localStorage.getItem(this.LATEST_ALERTS_KEY);

    if (!raw) {
      this.alerts = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw) as LatestAlert[] | null;

      this.alerts = Array.isArray(parsed)
        ? parsed.slice(0, this.maxSidebarCards)
        : [];
    } catch (e) {
      console.error('Failed to parse alerts from storage', e);
      this.alerts = [];
    }
  }

  viewAlert(alert: LatestAlert): void {
    const targetId = alert.posting_queue_id;

    this.router.navigate(['/preview-post', alert.format, targetId]);
  }

  viewRow(row: LatestPost): void {
    const targetId =
      row.enable_repeat === -1 ? row.parent_post_id ?? row.id : row.id;

    this.router.navigate(['/preview-post', row.tag, targetId]);
  }

  openDeleteConfirm() {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '380px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'confirm' && this.id) {
        this.deletePost();
      }
    });
  }

  deletePost() {
    this.coreService.postData(`manual-post/deletePost/${this.id}`, {
      company_id: this.companyID,
    }).subscribe(() => {
      this.router.navigate(['/posts']);
    });
  }

  backToPrevPage() {
    this.location.back();
  }
}